import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChatBubbleOutline,
  Done,
  DoneAll,
  Cancel,
  Delete,
  FileDownload,
  RestartAlt,
  ScienceOutlined,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridFilterOperator,
  type GridPaginationModel,
  type GridCellParams,
  type GridRenderCellParams,
  type GridRowSelectionModel,
  type GridSortModel,
  type MuiEvent,
} from '@mui/x-data-grid';

import {
  classifyFlowgateResultError,
  FLOWGATE_ROW_ID_COLUMN,
  getFlowgateResultDownloadUrl,
  useBulkCreateFlowgateSelections,
  useBulkDeleteFlowgateSelections,
  useBulkFirstValidate,
  useBulkSecondValidate,
  useCreateFlowgateSelection,
  useDeleteFlowgateSelection,
  useFlowgateResultQuery,
  useFlowgateResultQueryDomain,
  useFlowgateSelectionReject,
  useFlowgateSelectionResetValidation,
  useFlowgateSelections,
  useFlowgateSelectionsWithData,
  useFlowgateValidationStats,
  useUpdateFlowgateSelection,
  type FlowgateColumn,
  type FlowgateFilter,
  type FlowgateParquetDownloadResponse,
  type FlowgateQueryResponse,
  type FlowgateResultSchemaResponse,
  type FlowgateScalar,
  type FlowgateSelection,
  type FlowgateSort,
  type FlowgateValidationStats,
  type FlowgateValidationStatus,
  type FlowgateVariantReporting,
} from '@/api/flowgate';
import { get } from '@/api/httpClient';
import CustomNoRowsOverlay from '@/components/CustomNoRowsOverlay';
import FlowgateSelectionDrawer from './FlowgateSelectionDrawer';
import FlowgateAcmgDrawer from './FlowgateAcmgDrawer';
import FlowgateFilterPanel, {
  type FlowgateFilterValue,
} from './FlowgateFilterPanel';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_VISIBLE_COLUMN_COUNT = 5;
// Rank column is not among the first five, but it should still show by default.
const EXOMISER_RANK_RE = /^exomi[sz]er_rank$/i;
// Internal columns. Kept out of the Columns menu. The checkbox column stays on
// the grid so rows can still be selected; the others are not shown.
const PANEL_HIDDEN_FIELDS = new Set([
  '__check__',
  '__validation_status',
  '__comments',
  FLOWGATE_ROW_ID_COLUMN,
]);
/** ACMG column stays hidden until classification can be persisted. */
const SHOW_ACMG_COLUMN = false;
/** Reporting dropdown stays hidden until variant reporting is ready to show. */
const SHOW_REPORTING_COLUMN = false;

// Initial column visibility model — show the first N columns (excluding any
// force-hidden fields), hide the rest. `hiddenFields` are always hidden and
// don't count toward N. Internal columns passed in `hiddenFields` stay hidden
// and are also omitted from the Columns panel.
const buildInitialVisibility = (
  columns: FlowgateColumn[],
  hiddenFields: string[] = [],
  visibleCount = DEFAULT_VISIBLE_COLUMN_COUNT,
): GridColumnVisibilityModel => {
  const hidden = new Set(hiddenFields);
  const model: GridColumnVisibilityModel = {};
  let shown = 0;
  columns.forEach((c) => {
    if (hidden.has(c.name)) {
      model[c.name] = false;
    } else if (shown < visibleCount) {
      model[c.name] = true;
      shown += 1;
    } else {
      model[c.name] = false;
    }
  });
  // Virtual columns (not part of the schema) we also want hidden by default.
  hiddenFields.forEach((f) => {
    model[f] = false;
  });
  // First N stay as they are (uploaded variation, location, symbol, HGVSc,
  // existing variation). Also show the Exomiser rank column when present.
  columns.forEach((c) => {
    if (EXOMISER_RANK_RE.test(c.name)) {
      model[c.name] = true;
    }
  });
  return model;
};

// Toolbar with just the Columns button. Filtering is done through the dedicated
// Filters side panel (gene-aware + column predicates), so the grid's built-in
// filter UI is removed to avoid a redundant, confusing control. Export lives in
// our own menu outside the grid (see ExportMenu).
const DataGridToolbar = () => (
  <GridToolbarContainer
    sx={{
      minHeight: 44,
      px: 1,
      justifyContent: 'flex-end',
      bgcolor: 'background.default',
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <GridToolbarColumnsButton />
  </GridToolbarContainer>
);

// ── DuckDB → JS coercion ──────────────────────────────────────────────────────

type ColumnKind = 'number' | 'boolean' | 'string';

const NUMBER_TYPES = new Set(['BIGINT', 'INTEGER', 'DOUBLE', 'FLOAT']);

const columnKind = (type: FlowgateColumn['type']): ColumnKind => {
  const upper = type.toUpperCase();
  if (NUMBER_TYPES.has(upper) || upper.startsWith('DECIMAL')) {
    return 'number';
  }
  if (upper === 'BOOLEAN') {
    return 'boolean';
  }
  return 'string';
};

// Coerce a user-typed filter value (always a string in DataGrid) into the
// type the BE filter DSL expects for this column.
const coerceFilterValue = (
  raw: unknown,
  kind: ColumnKind,
): FlowgateScalar | null => {
  if (raw == null || raw === '') {
    return null;
  }
  if (kind === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (kind === 'boolean') {
    if (raw === true || raw === 'true') {
      return true;
    }
    if (raw === false || raw === 'false') {
      return false;
    }
    return null;
  }
  return String(raw);
};

// ── DataGrid filter operators per column kind ─────────────────────────────────
// Each operator carries the Flowgate DSL `op` it maps to in its `value`.

type FlowgateOperatorValue = {
  flowgateOp: FlowgateFilter['op'];
};

const makeOperator = (
  label: string,
  flowgateOp: FlowgateFilter['op'],
  requiresInput: boolean,
): GridFilterOperator & FlowgateOperatorValue => ({
  label,
  value: flowgateOp,
  flowgateOp,
  // We do server-side filtering, so getApplyFilterFn always passes. We only
  // need DataGrid to remember the filter state in its model.
  getApplyFilterFn: () => () => true,
  InputComponent: requiresInput ? undefined : undefined,
  requiresFilterValue: requiresInput,
});

const numberOperators: Array<GridFilterOperator & FlowgateOperatorValue> = [
  makeOperator('=', 'eq', true),
  makeOperator('≠', 'ne', true),
  makeOperator('<', 'lt', true),
  makeOperator('≤', 'le', true),
  makeOperator('>', 'gt', true),
  makeOperator('≥', 'ge', true),
  makeOperator('is empty', 'is_null', false),
  makeOperator('is not empty', 'not_null', false),
];

const stringOperators: Array<GridFilterOperator & FlowgateOperatorValue> = [
  makeOperator('=', 'eq', true),
  makeOperator('≠', 'ne', true),
  makeOperator('contains', 'contains', true),
  makeOperator('starts with', 'starts_with', true),
  makeOperator('is empty', 'is_null', false),
  makeOperator('is not empty', 'not_null', false),
];

const booleanOperators: Array<GridFilterOperator & FlowgateOperatorValue> = [
  makeOperator('= true', 'eq', false),
  makeOperator('= false', 'ne', false),
  makeOperator('is empty', 'is_null', false),
  makeOperator('is not empty', 'not_null', false),
];

const operatorsForKind = (kind: ColumnKind) => {
  if (kind === 'number') return numberOperators;
  if (kind === 'boolean') return booleanOperators;
  return stringOperators;
};

// ── Filter model → Flowgate DSL ───────────────────────────────────────────────

// Convert one DataGrid filter item to the Flowgate DSL. Returns null when the
// item is incomplete (no value yet) so it can be filtered out.
const itemToFlowgateFilter = (
  item: GridFilterModel['items'][number],
  columnKinds: Record<string, ColumnKind>,
): FlowgateFilter | null => {
  const column = item.field;
  const op = item.operator as FlowgateFilter['op'];
  const kind = columnKinds[column] ?? 'string';

  if (op === 'is_null' || op === 'not_null') {
    return { column, op };
  }
  if (op === 'in') {
    const arr = Array.isArray(item.value) ? item.value : [];
    const coerced = arr
      .map((v: unknown) => coerceFilterValue(v, kind))
      .filter((v): v is FlowgateScalar => v != null);
    return coerced.length === 0 ? null : { column, op: 'in', value: coerced };
  }
  // boolean "= true" / "= false" operators carry no input — derive value
  // from the operator and always emit as `eq`.
  if (kind === 'boolean') {
    if (op === 'eq') return { column, op: 'eq', value: true };
    if (op === 'ne') return { column, op: 'eq', value: false };
    return null;
  }
  const value = coerceFilterValue(item.value, kind);
  if (value == null) return null;
  return { column, op, value } as FlowgateFilter;
};

const toFlowgateFilters = (
  model: GridFilterModel,
  columnKinds: Record<string, ColumnKind>,
): FlowgateFilter[] =>
  model.items
    .map((item) => itemToFlowgateFilter(item, columnKinds))
    .filter((f): f is FlowgateFilter => f != null);

const toFlowgateSort = (model: GridSortModel): FlowgateSort[] =>
  model
    .filter((s) => s.sort === 'asc' || s.sort === 'desc')
    .map((s) => ({
      column: s.field,
      direction: s.sort as 'asc' | 'desc',
    }));

// ── Row zip ───────────────────────────────────────────────────────────────────

// `gridRowId` is the value MUI DataGrid uses as the row's stable identity. When
// the BE result is phase-2 it carries a `__row_id` column and we use that real
// value (also what every /flowgate/selections/* endpoint expects as `row_id`).
// For pre-phase-2 results without that column, fall back to the synthetic
// `${offset}-${i}` so the grid still renders.
type GridRow = Record<string, FlowgateScalar | null> & {
  gridRowId: string | number;
};

const VARIANT_LABEL_FIELDS = [
  '#Uploaded_variation',
  'Uploaded_variation',
  'uploaded_variation',
  'Location',
];

const variantLabelFromRow = (row: GridRow): string => {
  const keys = Object.keys(row);
  const match = VARIANT_LABEL_FIELDS.map((wanted) =>
    keys.find((k) => k.toLowerCase() === wanted.toLowerCase()),
  ).find((k): k is string => !!k);
  if (match) {
    const value = row[match];
    if (value != null && String(value).trim() !== '' && String(value) !== '-') {
      return String(value);
    }
  }
  return String(row.gridRowId);
};

/** Keep ACMG / comment clicks from bubbling into checkbox selection. */
const stopGridSelection = (event: SyntheticEvent) => {
  event.stopPropagation();
};

const zipRows = (
  response: FlowgateQueryResponse,
  offset: number,
): GridRow[] => {
  const rowIdIdx = response.columns.indexOf(FLOWGATE_ROW_ID_COLUMN);
  return response.rows.map((row, i) => {
    const realRowId = rowIdIdx >= 0 ? row[rowIdIdx] : null;
    const obj: GridRow = {
      gridRowId:
        realRowId == null ? `${offset}-${i}` : (realRowId as string | number),
    } as GridRow;
    response.columns.forEach((col, j) => {
      obj[col] = row[j];
    });
    return obj;
  });
};

// ── Export menu ───────────────────────────────────────────────────────────────

export const ExportMenu = ({ resultId }: { resultId: string }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [parquetLoading, setParquetLoading] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchor(event.currentTarget);
  const handleClose = () => setAnchor(null);

  const downloadStreamed = (format: 'csv' | 'tsv') => {
    window.open(getFlowgateResultDownloadUrl(resultId, format), '_blank');
    handleClose();
  };

  const downloadParquet = async () => {
    setParquetLoading(true);
    try {
      const resp = await get<FlowgateParquetDownloadResponse>(
        `flowgate/results/${resultId}/download/?format=parquet`,
      );
      window.open(resp.data.url, '_blank');
    } catch {
      // Errors are surfaced via global snackbars elsewhere; nothing to do here.
    } finally {
      setParquetLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FileDownload />}
        onClick={handleClick}
        size="small"
      >
        Export
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={handleClose}>
        <MenuItem onClick={() => downloadStreamed('csv')}>CSV</MenuItem>
        <MenuItem onClick={() => downloadStreamed('tsv')}>TSV</MenuItem>
        <MenuItem onClick={downloadParquet} disabled={parquetLoading}>
          Parquet{parquetLoading ? '…' : ''}
        </MenuItem>
      </Menu>
    </>
  );
};

// ── Empty / error states ──────────────────────────────────────────────────────

const EmptyOverlay = () => <CustomNoRowsOverlay message="No matching rows." />;

// ── Selection / validation helpers ────────────────────────────────────────────

const STATUS_LABEL: Record<FlowgateValidationStatus, string> = {
  pending: 'Pending',
  first_validated: '1st validated',
  second_validated: '2nd validated',
  rejected: 'Rejected',
};

const STATUS_COLOR: Record<
  FlowgateValidationStatus,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  pending: 'default',
  first_validated: 'info',
  second_validated: 'success',
  rejected: 'error',
};

const REPORTING_OPTIONS: { value: FlowgateVariantReporting; label: string }[] =
  [
    { value: 'primary_finding', label: 'Primary' },
    { value: 'secondary_finding', label: 'Secondary' },
    { value: 'incidental_finding', label: 'Incidental' },
  ];

// Banner shown when the result predates the phase-2 `__row_id` column. The
// selection / validation UI is hidden in this case.
const NoRowIdBanner = () => (
  <Alert severity="info" sx={{ mb: 2 }}>
    Selection and validation are unavailable for this result because it was
    produced before row identity was supported. Re-run the pipeline to enable
    selection.
  </Alert>
);

// Compact stats strip — Total / Pending / 1st / 2nd / Rejected.
const StatsStrip = ({ stats }: { stats: FlowgateValidationStats | null }) => {
  if (!stats) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Validation status
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`Total ${stats.total}`} variant="outlined" size="small" />
        <Chip label={`Pending ${stats.pending}`} color="default" size="small" />
        <Chip
          label={`1st ${stats.first_validated}`}
          color="info"
          size="small"
        />
        <Chip
          label={`2nd ${stats.second_validated}`}
          color="success"
          size="small"
        />
        <Chip label={`Rejected ${stats.rejected}`} color="error" size="small" />
      </Stack>
    </Paper>
  );
};

// Action bar over the grid for the current selection. Which buttons show
// depends on the step mode: first-validate in the First Validation step,
// second-validate + reject in Second Validation; Reset/Delete in both.
export type FlowgateGridMode =
  | 'select'
  | 'first_validation'
  | 'second_validation';

type SelectionActionsBarProps = {
  mode: FlowgateGridMode;
  selectedSelectionIds: string[];
  onFirstValidate: () => void;
  onSecondValidate: () => void;
  onReject: () => void;
  onReset: () => void;
  onDelete: () => void;
  busy: boolean;
};

const SelectionActionsBar = ({
  mode,
  selectedSelectionIds,
  onFirstValidate,
  onSecondValidate,
  onReject,
  onReset,
  onDelete,
  busy,
}: SelectionActionsBarProps) => {
  const n = selectedSelectionIds.length;
  const disabled = n === 0 || busy;
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', lg: 'center' }}
      >
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {n}
          </Box>{' '}
          selected for validation
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {mode === 'first_validation' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Done />}
              onClick={onFirstValidate}
              disabled={disabled}
            >
              First validate
            </Button>
          )}
          {mode === 'second_validation' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<DoneAll />}
              onClick={onSecondValidate}
              disabled={disabled}
            >
              Second validate
            </Button>
          )}
          {/* Reject is available in both validation steps; the handler picks the
              round_number per selection (1 before first-validate, else 2). */}
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Cancel />}
            onClick={onReject}
            disabled={disabled}
          >
            Reject
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={onReset}
            disabled={disabled}
          >
            Reset
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={onDelete}
            disabled={disabled}
          >
            Remove
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ── Virtual selection cells ───────────────────────────────────────────────────
// Hoisted so they're stable references (react/no-unstable-nested-components).

const ValidationStatusCell = ({
  selection,
}: {
  selection: FlowgateSelection | undefined;
}) => {
  if (!selection) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[selection.validation_status]}
      color={STATUS_COLOR[selection.validation_status]}
    />
  );
};

type ReportingCellProps = {
  selection: FlowgateSelection | undefined;
  onChange: (
    selectionId: string,
    value: FlowgateVariantReporting | null,
  ) => void;
};

const VariantReportingCell = ({ selection, onChange }: ReportingCellProps) => {
  if (!selection) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Select
      size="small"
      variant="standard"
      value={selection.variant_reporting ?? ''}
      onChange={(e) => {
        const v = e.target.value as FlowgateVariantReporting | '';
        onChange(selection.id, v === '' ? null : v);
      }}
      sx={{ minWidth: 140 }}
    >
      <MenuItem value="">—</MenuItem>
      {REPORTING_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};

type CommentsCellProps = {
  selection: FlowgateSelection | undefined;
  onOpen: (selectionId: string) => void;
};

const CommentsCell = ({ selection, onOpen }: CommentsCellProps) => {
  if (!selection) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const count = selection.comments?.length ?? 0;
  return (
    <Tooltip title={count === 0 ? 'Add comment' : `${count} comment(s)`}>
      <IconButton
        size="small"
        onMouseDown={stopGridSelection}
        onClick={(event) => {
          stopGridSelection(event);
          onOpen(selection.id);
        }}
      >
        <Badge badgeContent={count} color="primary">
          <ChatBubbleOutline fontSize="small" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};

type AcmgCellProps = {
  onOpen: () => void;
};

const AcmgCell = ({ onOpen }: AcmgCellProps) => (
  <Tooltip title="ACMG classification">
    <IconButton
      size="small"
      aria-label="Open ACMG classification"
      onMouseDown={stopGridSelection}
      onClick={(event) => {
        stopGridSelection(event);
        onOpen();
      }}
    >
      <ScienceOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
);

// ── Grid (one step's worth of data + actions) ────────────────────────────────

type FlowgateResultGridProps = {
  resultId: string;
  schema: FlowgateResultSchemaResponse;
  mode: FlowgateGridMode;
};

const FlowgateResultGrid = ({
  resultId,
  schema,
  mode,
}: FlowgateResultGridProps) => {
  const query = useFlowgateResultQuery();
  const queryDomain = useFlowgateResultQueryDomain();

  // Phase-2 feature flag: results predating __row_id can't be selected.
  const hasRowId = !!schema.has_row_id;

  // Step 0 ('select') reads the full table via /query/ (or /query-domain/ once a
  // domain filter is applied). Validation steps read the user's selected rows
  // via /selections/with-data/.
  const view: 'all' | 'mine' = mode === 'select' ? 'all' : 'mine';

  // Domain filter (set by the side panel on Apply); null → plain /query/.
  const [domainFilter, setDomainFilter] = useState<FlowgateFilterValue | null>(
    null,
  );

  // Selections + stats (only fetched when has_row_id; the hooks are gated
  // internally by enabled).
  const { data: selectionsList } = useFlowgateSelections({
    resultId: hasRowId ? resultId : undefined,
  });
  const { data: validationStats } = useFlowgateValidationStats(
    hasRowId ? resultId : undefined,
  );
  const { data: selectionsWithData, refetch: refetchSelectionsWithData } =
    useFlowgateSelectionsWithData(
      hasRowId && view === 'mine' ? resultId : undefined,
    );

  const createSelection = useCreateFlowgateSelection();
  const deleteSelection = useDeleteFlowgateSelection();
  const bulkCreate = useBulkCreateFlowgateSelections();
  const bulkDelete = useBulkDeleteFlowgateSelections();
  const bulkFirstValidate = useBulkFirstValidate();
  const bulkSecondValidate = useBulkSecondValidate();
  const rejectSelection = useFlowgateSelectionReject();
  const resetSelectionValidation = useFlowgateSelectionResetValidation();
  const updateSelection = useUpdateFlowgateSelection();

  const mutationBusy =
    createSelection.isPending ||
    deleteSelection.isPending ||
    bulkCreate.isPending ||
    bulkDelete.isPending ||
    bulkFirstValidate.isPending ||
    bulkSecondValidate.isPending ||
    rejectSelection.isPending ||
    resetSelectionValidation.isPending ||
    updateSelection.isPending;

  // Drawer (comments thread).
  const [drawerSelectionId, setDrawerSelectionId] = useState<string | null>(
    null,
  );

  // ACMG calculator (frontend-only; opening a row must not persist a selection).
  const [acmgRow, setAcmgRow] = useState<GridRow | null>(null);

  const [rows, setRows] = useState<GridRow[]>([]);
  const [total, setTotal] = useState(0);
  // Column visibility — first N visible by default, user toggles via the
  // toolbar's Columns panel. Re-initialized whenever the schema changes
  // (different result id → different column set).
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  useEffect(() => {
    if (schema) {
      setColumnVisibilityModel(
        buildInitialVisibility(schema.columns, [
          FLOWGATE_ROW_ID_COLUMN,
          '__validation_status',
          '__comments',
        ]),
      );
    }
  }, [schema]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  // The grid's built-in column filter is disabled (filtering goes through the
  // Filters side panel), so this stays an empty model; runQuery still merges it
  // (a no-op) alongside the panel's predicates.
  const [filterModel] = useState<GridFilterModel>({ items: [] });
  const [queryError, setQueryError] = useState<ReturnType<
    typeof classifyFlowgateResultError
  > | null>(null);

  // Map column name → ColumnKind, used for filter-value coercion.
  const columnKinds = useMemo<Record<string, ColumnKind>>(() => {
    if (!schema) return {};
    return Object.fromEntries(
      schema.columns.map((c) => [c.name, columnKind(c.type)]),
    );
  }, [schema]);

  // ── Selection lookup helpers ─────────────────────────────────────────────────

  // row_id → FlowgateSelection (for joining grid rows with selection state).
  // Keys are stringified so they match DataGrid's `string | number` ids.
  const selectionByRowId = useMemo<Map<string, FlowgateSelection>>(() => {
    const m = new Map<string, FlowgateSelection>();
    selectionsList?.forEach((s) => m.set(String(s.row_id), s));
    return m;
  }, [selectionsList]);

  // Row ids of the user's persisted selections — the checked rows in the
  // select step.
  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => Array.from(selectionByRowId.keys()),
    [selectionByRowId],
  );

  // Transient "which rows to act on" pick for the validation steps. Checking a
  // row here does NOT create/delete a persisted selection — it only chooses the
  // target of the validate/reject/reset/remove actions. Reset when the step
  // (mode) changes so picks don't bleed across First/Second validation.
  const [validationPicks, setValidationPicks] = useState<GridRowSelectionModel>(
    [],
  );
  useEffect(() => {
    setValidationPicks([]);
  }, [mode]);

  // Selection ids targeted by the action bar = the picked rows mapped to their
  // selection ids.
  const selectedSelectionIds = useMemo<string[]>(
    () =>
      validationPicks
        .map((rid) => selectionByRowId.get(String(rid))?.id)
        .filter((id): id is string => !!id),
    [validationPicks, selectionByRowId],
  );

  const surfaceError = useCallback((err: unknown) => {
    const info = classifyFlowgateResultError(err);
    enqueueSnackbar(info.message, {
      variant: info.kind === 'not_ready' ? 'warning' : 'error',
    });
  }, []);

  // ── Row selection toggle: persist immediately to BE ──────────────────────────

  const handleRowSelectionModelChange = useCallback(
    (next: GridRowSelectionModel) => {
      if (!resultId || !hasRowId) return;
      const nextSet = new Set(next.map(String));
      const prevSet = new Set(rowSelectionModel.map(String));

      const added = [...nextSet].filter((id) => !prevSet.has(id));
      const removed = [...prevSet].filter((id) => !nextSet.has(id));

      // Bulk create on multi-add (select-all-on-page).
      if (added.length > 1) {
        bulkCreate.mutate(
          {
            flowgate_result_id: resultId,
            row_ids: added.map(Number),
          },
          { onError: surfaceError },
        );
      } else if (added.length === 1) {
        createSelection.mutate(
          { flowgate_result_id: resultId, row_id: Number(added[0]) },
          { onError: surfaceError },
        );
      }

      // Bulk delete on multi-remove.
      if (removed.length > 1) {
        bulkDelete.mutate(
          {
            flowgate_result_id: resultId,
            row_ids: removed.map(Number),
            resultId,
          },
          { onError: surfaceError },
        );
      } else if (removed.length === 1) {
        const sel = selectionByRowId.get(removed[0]);
        if (sel) {
          deleteSelection.mutate(
            { selectionId: sel.id, resultId },
            { onError: surfaceError },
          );
        }
      }
    },
    [
      resultId,
      hasRowId,
      rowSelectionModel,
      bulkCreate,
      createSelection,
      bulkDelete,
      deleteSelection,
      selectionByRowId,
      surfaceError,
    ],
  );

  // ── Selection action handlers ────────────────────────────────────────────────

  const handleFirstValidate = useCallback(() => {
    if (selectedSelectionIds.length === 0) return;
    bulkFirstValidate.mutate(
      { selection_ids: selectedSelectionIds, resultId },
      { onError: surfaceError },
    );
  }, [bulkFirstValidate, selectedSelectionIds, resultId, surfaceError]);

  const handleSecondValidate = useCallback(() => {
    if (selectedSelectionIds.length === 0) return;
    bulkSecondValidate.mutate(
      { selection_ids: selectedSelectionIds, resultId },
      { onError: surfaceError },
    );
  }, [bulkSecondValidate, selectedSelectionIds, resultId, surfaceError]);

  const handleReject = useCallback(() => {
    if (selectedSelectionIds.length === 0) return;
    // The per-selection reject endpoint requires round_number. We invoke it
    // per selection; the BE figures out which round the user is currently in.
    selectedSelectionIds.forEach((id) => {
      const sel = selectionsList?.find((s) => s.id === id);
      const roundNumber: 1 | 2 = sel?.first_validated ? 2 : 1;
      rejectSelection.mutate(
        { selectionId: id, round_number: roundNumber, resultId },
        { onError: surfaceError },
      );
    });
  }, [
    selectedSelectionIds,
    selectionsList,
    rejectSelection,
    resultId,
    surfaceError,
  ]);

  const handleReset = useCallback(() => {
    if (selectedSelectionIds.length === 0) return;
    selectedSelectionIds.forEach((id) => {
      resetSelectionValidation.mutate(
        { selectionId: id, resultId },
        { onError: surfaceError },
      );
    });
  }, [selectedSelectionIds, resetSelectionValidation, resultId, surfaceError]);

  const handleDelete = useCallback(() => {
    if (selectedSelectionIds.length === 0 || !resultId) return;
    bulkDelete.mutate(
      { selection_ids: selectedSelectionIds, resultId },
      { onError: surfaceError },
    );
  }, [selectedSelectionIds, bulkDelete, resultId, surfaceError]);

  const handleReportingChange = useCallback(
    (selectionId: string, value: FlowgateVariantReporting | null) => {
      updateSelection.mutate(
        { selectionId, resultId, body: { variant_reporting: value } },
        { onError: surfaceError },
      );
    },
    [updateSelection, resultId, surfaceError],
  );

  // ── Columns: prepend virtual selection columns when has_row_id ───────────────

  const columns = useMemo<GridColDef<GridRow>[]>(() => {
    if (!schema) return [];
    const dataCols: GridColDef<GridRow>[] = schema.columns.map((c) => {
      const kind = columnKinds[c.name];
      return {
        field: c.name,
        headerName: c.name,
        flex: 1,
        sortable: true,
        filterable: true,
        hideable: !PANEL_HIDDEN_FIELDS.has(c.name),
        filterOperators: operatorsForKind(kind),
        type: kind,
      };
    });

    const acmgCol: GridColDef<GridRow> | null = SHOW_ACMG_COLUMN
      ? {
          field: '__acmg',
          headerName: 'ACMG',
          width: 72,
          sortable: false,
          filterable: false,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params: GridRenderCellParams<GridRow>) => (
            <AcmgCell onOpen={() => setAcmgRow(params.row)} />
          ),
        }
      : null;

    if (!hasRowId) return acmgCol ? [acmgCol, ...dataCols] : dataCols;

    const reportingCol: GridColDef<GridRow> | null = SHOW_REPORTING_COLUMN
      ? {
          field: '__variant_reporting',
          headerName: 'Reporting',
          width: 160,
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams<GridRow>) => (
            <VariantReportingCell
              selection={selectionByRowId.get(String(params.id))}
              onChange={handleReportingChange}
            />
          ),
        }
      : null;

    const virtualCols: GridColDef<GridRow>[] = [
      {
        field: '__validation_status',
        headerName: 'Status',
        width: 140,
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: (params: GridRenderCellParams<GridRow>) => (
          <ValidationStatusCell
            selection={selectionByRowId.get(String(params.id))}
          />
        ),
      },
      ...(reportingCol ? [reportingCol] : []),
      {
        field: '__comments',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        hideable: false,
        align: 'center',
        renderCell: (params: GridRenderCellParams<GridRow>) => (
          <CommentsCell
            selection={selectionByRowId.get(String(params.id))}
            onOpen={setDrawerSelectionId}
          />
        ),
      },
      ...(acmgCol ? [acmgCol] : []),
    ];
    return [...virtualCols, ...dataCols];
  }, [schema, columnKinds, hasRowId, selectionByRowId, handleReportingChange]);

  // ── Data fetching: /query/ for view='all', /with-data/ for view='mine' ──────

  const runQuery = useCallback(() => {
    if (!resultId || !schema) return;
    if (view === 'mine') {
      // Use the cached selections-with-data response.
      const data = selectionsWithData?.data ?? null;
      if (data) {
        setRows(zipRows(data, 0));
        setTotal(data.total);
      } else {
        setRows([]);
        setTotal(0);
      }
      setQueryError(null);
      return;
    }

    const offset = paginationModel.page * paginationModel.pageSize;
    setQueryError(null);
    const onSuccess = ({ data }: { data: FlowgateQueryResponse }) => {
      setRows(zipRows(data, offset));
      setTotal(data.total);
    };
    const onError = (err: unknown) =>
      setQueryError(classifyFlowgateResultError(err));
    const genericFilters = toFlowgateFilters(filterModel, columnKinds);
    const sort = toFlowgateSort(sortModel);

    // Once a domain filter is applied, route through /query-domain/ so the BE
    // resolves HPO/gene/panel filters server-side. The panel's own column
    // predicates are merged with the DataGrid per-column filters.
    if (domainFilter) {
      queryDomain.mutate(
        {
          resultId,
          body: {
            domain_filters: domainFilter.domainFilters,
            filters: [...domainFilter.filters, ...genericFilters],
            sort,
            limit: paginationModel.pageSize,
            offset,
          },
        },
        { onSuccess, onError },
      );
    } else {
      query.mutate(
        {
          resultId,
          body: {
            filters: genericFilters,
            sort,
            limit: paginationModel.pageSize,
            offset,
          },
        },
        { onSuccess, onError },
      );
    }
    // `query`/`queryDomain` are stable mutation handles for the page lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resultId,
    schema,
    view,
    domainFilter,
    selectionsWithData,
    paginationModel,
    sortModel,
    filterModel,
    columnKinds,
  ]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  // Re-fetch with-data when entering "mine" view, in case selections changed.
  useEffect(() => {
    if (view === 'mine' && hasRowId) {
      refetchSelectionsWithData();
    }
  }, [view, hasRowId, refetchSelectionsWithData]);

  const handleApplyDomainFilter = useCallback((value: FlowgateFilterValue) => {
    const hasAny =
      Object.keys(value.domainFilters).length > 0 || value.filters.length > 0;
    setDomainFilter(hasAny ? value : null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleClearDomainFilter = useCallback(() => {
    setDomainFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  const selectMode = mode === 'select';
  // Checkboxes are shown whenever the result supports selection. In the select
  // step they toggle persisted selections; in the validation steps they pick
  // which selected rows the action bar targets.
  const selectable = hasRowId;

  const handleActionCellClick = useCallback(
    (params: GridCellParams<GridRow>, event: MuiEvent<MouseEvent>) => {
      if (params.field === '__acmg' || params.field === '__comments') {
        event.stopPropagation();
        // MUI DataGrid uses this flag to skip built-in cell/row selection.
        // eslint-disable-next-line no-param-reassign
        event.defaultMuiPrevented = true;
      }
    },
    [],
  );

  const grid = (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      {!hasRowId && <NoRowIdBanner />}
      {hasRowId && !selectMode && (
        <StatsStrip stats={validationStats ?? null} />
      )}
      {hasRowId && !selectMode && (
        <SelectionActionsBar
          mode={mode}
          selectedSelectionIds={selectedSelectionIds}
          onFirstValidate={handleFirstValidate}
          onSecondValidate={handleSecondValidate}
          onReject={handleReject}
          onReset={handleReset}
          onDelete={handleDelete}
          busy={mutationBusy}
        />
      )}

      {queryError && (
        <Alert
          severity={queryError.kind === 'not_ready' ? 'warning' : 'error'}
          action={
            <Button color="inherit" size="small" onClick={runQuery}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {queryError.message}
        </Alert>
      )}

      {/* Result count for the current query (reflects applied filters). */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {query.isPending || queryDomain.isPending
            ? 'Loading…'
            : `${total.toLocaleString()} result${total === 1 ? '' : 's'}`}
        </Typography>
        {selectMode && domainFilter && (
          <Chip size="small" variant="outlined" label="filtered" />
        )}
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.gridRowId}
          rowCount={total}
          loading={query.isPending || queryDomain.isPending}
          paginationMode={view === 'all' ? 'server' : 'client'}
          sortingMode={view === 'all' ? 'server' : 'client'}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          // Filtering is driven by the Filters side panel, not the grid's own
          // filter UI — disable the built-in column filter entirely.
          disableColumnFilter
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          pageSizeOptions={[25, 50, 100, 250]}
          checkboxSelection={selectable}
          keepNonExistentRowsSelected={selectable}
          // Select step drives the persisted-selection model; validation steps
          // drive the transient pick model. (checkboxSelection is on in both,
          // so a multi-item model is allowed.)
          rowSelectionModel={
            // eslint-disable-next-line no-nested-ternary
            selectable
              ? selectMode
                ? rowSelectionModel
                : validationPicks
              : undefined
          }
          onRowSelectionModelChange={
            // eslint-disable-next-line no-nested-ternary
            selectable
              ? selectMode
                ? handleRowSelectionModelChange
                : setValidationPicks
              : undefined
          }
          isRowSelectable={() => selectable}
          disableRowSelectionOnClick
          onCellClick={handleActionCellClick}
          autoHeight
          slots={{
            noRowsOverlay: EmptyOverlay,
            toolbar: DataGridToolbar,
          }}
          slotProps={{
            columnsManagement: {
              getTogglableColumns: (cols) =>
                cols
                  .filter((col) => !PANEL_HIDDEN_FIELDS.has(col.field))
                  .map((col) => col.field),
            },
          }}
          sx={{
            '--DataGrid-overlayHeight': '120px',
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'background.default',
              borderBottomColor: 'divider',
            },
            '& .MuiDataGrid-cell': {
              borderBottomColor: 'divider',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTopColor: 'divider',
            },
          }}
        />
      </Paper>

      {drawerSelectionId && (
        <FlowgateSelectionDrawer
          selectionId={drawerSelectionId}
          resultId={resultId}
          onClose={() => setDrawerSelectionId(null)}
        />
      )}
      {SHOW_ACMG_COLUMN && (
        <FlowgateAcmgDrawer
          open={!!acmgRow}
          rowId={acmgRow?.gridRowId ?? null}
          variantLabel={acmgRow ? variantLabelFromRow(acmgRow) : ''}
          onClose={() => setAcmgRow(null)}
        />
      )}
    </Box>
  );

  // Step 0 puts the gene-aware filter panel beside the grid.
  if (selectMode) {
    return (
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        sx={{ alignItems: 'stretch' }}
      >
        <FlowgateFilterPanel
          columns={schema.columns}
          onApply={handleApplyDomainFilter}
          onClear={handleClearDomainFilter}
        />
        {grid}
      </Stack>
    );
  }

  return grid;
};

export default FlowgateResultGrid;
